import React, { useEffect, useState } from "react";
import {
  StyleSheet,
  View,
  Text,
  Modal,
  Pressable,
  LayoutRectangle,
  findNodeHandle,
  UIManager,
  Platform,
  useWindowDimensions,
} from "react-native";
import { ADMIN_TOKENS } from "../../../styles/adminTokens";

type TooltipProps = {
  text: string;
  targetRef: React.RefObject<any>;
  onDismiss: () => void;
  maxWidth?: number;
};

const Tooltip: React.FC<TooltipProps> = ({
  text,
  targetRef,
  onDismiss,
  maxWidth = 260,
}) => {
  const [targetLayout, setTargetLayout] = useState<LayoutRectangle | null>(
    null,
  );
  const { width: viewportWidth } = useWindowDimensions();

  const measureTarget = (
    node: any,
    cb: (x: number, y: number, w: number, h: number) => void,
  ) => {
    if (Platform.OS === "web") {
      const rect = node.getBoundingClientRect();
      cb(rect.left, rect.top, rect.width, rect.height);
      return;
    }

    const handle = findNodeHandle(node);
    if (!handle) return;
    UIManager.measureInWindow(handle, (x, y, width, height) => {
      cb(x, y, width, height);
    });
  };

  useEffect(() => {
    if (!targetRef.current) return;

    measureTarget(targetRef.current, (x, y, width, height) => {
      setTargetLayout({ x, y, width, height });
    });

    const timer = setTimeout(() => {
      if (!targetRef.current) return;
      measureTarget(targetRef.current, (x, y, width, height) => {
        setTargetLayout({ x, y, width, height });
      });
    }, 0);

    return () => clearTimeout(timer);
  }, [targetRef, text]);

  if (!targetLayout) return null;

  const horizontalMargin = ADMIN_TOKENS.spacing.lg;
  const tooltipTop =
    targetLayout.y + targetLayout.height + ADMIN_TOKENS.spacing.sm;
  const maxLeft = Math.max(
    horizontalMargin,
    viewportWidth - maxWidth - horizontalMargin,
  );
  const tooltipLeft = Math.min(
    Math.max(targetLayout.x, horizontalMargin),
    maxLeft,
  );

  return (
    <Modal transparent animationType="fade" onRequestClose={onDismiss}>
      <Pressable style={styles.overlay} onPress={onDismiss}>
        <View
          style={[
            styles.tooltipContainer,
            {
              top: tooltipTop,
              left: tooltipLeft,
              maxWidth,
            },
          ]}
        >
          <Text testID="tooltipText" style={styles.tooltipText}>
            {text}
          </Text>
        </View>
      </Pressable>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
  },
  tooltipContainer: {
    position: "absolute",
    backgroundColor: ADMIN_TOKENS.color.textPrimary,
    paddingVertical: ADMIN_TOKENS.spacing.md,
    paddingHorizontal: ADMIN_TOKENS.spacing.lg,
    borderRadius: ADMIN_TOKENS.radius.lg,
    zIndex: ADMIN_TOKENS.zIndex.above,
    ...ADMIN_TOKENS.shadow.card,
  },
  tooltipText: {
    color: ADMIN_TOKENS.color.surface,
    fontFamily: ADMIN_TOKENS.font.medium,
    fontSize: ADMIN_TOKENS.fontSize.sm,
    lineHeight: ADMIN_TOKENS.lineHeight.sm,
  },
});

export default Tooltip;
